import { toast } from 'sonner'

import { useEffect, useState } from 'react'

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Label, PosButton, Spinner } from '@/components/ui'
import { QtyStepper } from '@/components/pos/cart/QtyStepper'
import { PAYMENT_METHODS } from '@/constants/payment'
import { useReturnItems } from '@/hooks/features/orders'
import { cn } from '@/lib/utils'
import { usePrinter } from '@/services/printer'
import { PosModel } from '@/types'
import { formatMoney } from '@/utils'

const round = (n: number) => Math.round(n * 100) / 100

/**
 * Return some or all of a sale (the reference's partial return): how many of each item come back, an optional return
 * charge kept back, and how the refund is paid. Refunds go out by the sale's method by default.
 */
export const ReturnDialog = ({ order, open, onOpenChange }: { order: PosModel.Order; open: boolean; onOpenChange: (open: boolean) => void }) => {
  const returnItems = useReturnItems()
  const { printSlip } = usePrinter()
  const lines = (order.returnable ?? []).filter((l) => l.remaining > 0)
  const [qty, setQty] = useState<Record<string, number>>({})
  const [charge, setCharge] = useState('')
  const [method, setMethod] = useState<PosModel.PaymentMethod>('cash')

  useEffect(() => {
    if (!open) return
    setQty({})
    setCharge('')
    setMethod(order.payments.length === 1 ? order.payments[0].method : 'cash')
  }, [open, order])

  const total = round(lines.reduce((s, l) => s + l.unit_paid * (qty[l.item_id] ?? 0), 0))
  const kept = round(Math.min(parseFloat(charge) || 0, total))
  const refund = round(total - kept)

  const confirm = () =>
    returnItems.mutate(
      {
        id: order._id,
        request: {
          lines: lines.map((l) => ({ item_id: l.item_id, quantity: qty[l.item_id] ?? 0 })),
          return_charge: kept,
          refund_method: method,
        },
      },
      {
        onSuccess: (updated) => {
          toast.success(`${formatMoney(refund)} refunded`, { description: 'Items go back to stock when the return syncs.' })
          if (updated.last_return) {
            printSlip({
              title: 'RETURN',
              ref: updated.last_return.ref,
              created_at: new Date().toISOString(),
              rows: [
                ['Sale', order.number],
                ...(updated.last_return.charge > 0 ? ([['Return charge', updated.last_return.charge.toFixed(2)]] as [string, string][]) : []),
                ['Refunded', updated.last_return.refund.toFixed(2)],
              ],
            })
          }
          onOpenChange(false)
        },
        onError: (e) => toast.error(e.message),
      }
    )

  return (
    <Dialog open={open} onOpenChange={(o) => !returnItems.isPending && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Return items · {order.number}</DialogTitle>
          <DialogDescription>Choose what comes back. Each item refunds what was paid for it.</DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-border-subtle rounded-md border border-border">
          {lines.map((l) => (
            <li key={l.item_id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="min-w-0">
                <span className="block truncate text-ink">{l.name}</span>
                <span className="text-xs text-muted-foreground">
                  {formatMoney(l.unit_paid)} each · {l.remaining} of {l.quantity} can come back
                </span>
              </span>
              <QtyStepper value={qty[l.item_id] ?? 0} max={l.remaining} onChange={(v) => setQty({ ...qty, [l.item_id]: Math.min(Math.max(v, 0), l.remaining) })} />
            </li>
          ))}
        </ul>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="return-charge">Return charge</Label>
            <Input id="return-charge" inputMode="decimal" value={charge} onChange={(e) => setCharge(e.target.value.replace(/[^\d.]/g, ''))} placeholder="0" className="h-11 text-right tabular-nums" />
          </div>
          <div className="space-y-1.5">
            <Label>Refund by</Label>
            <div className="flex flex-wrap gap-1.5">
              {PAYMENT_METHODS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethod(m.id)}
                  className={cn('h-11 rounded-md border px-3 text-sm', method === m.id ? 'border-primary bg-primary text-white' : 'border-input bg-card text-ink')}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter className="items-center justify-between sm:justify-between">
          <span className="text-heading-md tabular-nums text-ink">Refund {formatMoney(refund)}</span>
          <PosButton variant="destructive" size="touch" disabled={total <= 0 || returnItems.isPending} onClick={confirm}>
            {returnItems.isPending ? <Spinner /> : 'Return'}
          </PosButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
